import React, { useState, useEffect } from "react";
import {
  getReadableCustomerTextColor,
  hexToRgba,
} from "../../lib/linkInBio/linkInBioTheme";

function linkExternalProps(href) {
  if (href.startsWith("http://") || href.startsWith("https://")) {
    return { target: "_blank", rel: "noopener noreferrer" };
  }
  return {};
}

const secondaryClassName =
  "flex min-h-12 w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-3 text-center text-sm font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2";

/** Label color is set via `primaryStyle.color` so public `/{project}/{slug}` (light root) is not affected by global `:not(.dark) .text-white { @apply text-gray-900 }` in index.css. */
const primaryStaticClassName =
  "flex min-h-12 w-full items-center justify-center rounded-xl px-4 py-3 text-center text-sm font-semibold uppercase tracking-wide transition hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";

/**
 * Shared layout for public link-in-bio (interactive) and dashboard preview (non-interactive).
 */
const LinkInBioPublicView = ({
  profile,
  primaryResolved,
  primaryLabel,
  poweredByLabel,
  interactive = true,
  adaptCustomerContrast = false,
  onTrackedButtonClick,
  className = "",
}) => {
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => {
    setLogoFailed(false);
  }, [profile.logoUrl]);
  const initial = profile.displayName.charAt(0).toUpperCase() || "?";
  const Heading = interactive ? "h1" : "h2";

  const logoBg = profile.themeLogoBg;
  const primaryBg = profile.themePrimaryBg;
  const logoTileShadow = `0 10px 15px -3px ${hexToRgba(logoBg, 0.2)}`;
  const primaryShadow = `0 4px 6px -1px ${hexToRgba(primaryBg, 0.25)}`;

  const notifyTrack = (buttonKey) => {
    if (!interactive || !onTrackedButtonClick) {
      return;
    }
    onTrackedButtonClick(buttonKey);
  };

  const primaryStyle = {
    backgroundColor: primaryBg,
    boxShadow: primaryShadow,
    color: adaptCustomerContrast
      ? getReadableCustomerTextColor(primaryBg)
      : "#ffffff",
  };

  return (
    <div className={className}>
      <div className="mx-auto flex w-full max-w-md flex-col items-center text-center">
        <div
          className="mb-4 h-20 w-20 shrink-0 overflow-hidden rounded-2xl shadow-lg"
          style={{
            backgroundColor: logoBg,
            boxShadow: logoTileShadow,
          }}
        >
          {profile.logoUrl && !logoFailed ? (
            <img
              src={profile.logoUrl}
              alt={profile.displayName}
              className="h-full w-full object-cover"
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center text-3xl font-bold"
              style={{
                color: adaptCustomerContrast
                  ? getReadableCustomerTextColor(logoBg)
                  : "#ffffff",
              }}
            >
              {initial}
            </div>
          )}
        </div>

        <Heading className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
          {profile.displayName}
        </Heading>
        {profile.tagline ? (
          <p className="mt-2 max-w-sm text-sm leading-relaxed whitespace-pre-wrap text-slate-600">
            {profile.tagline}
          </p>
        ) : null}

        <div className="mt-8 w-full space-y-3">
          {primaryResolved ? (
            interactive ? (
              <a
                href={primaryResolved.href}
                {...linkExternalProps(primaryResolved.href)}
                className={`${primaryStaticClassName} focus-visible:ring-2 focus-visible:ring-white/90 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent`}
                style={primaryStyle}
                onClick={() => notifyTrack("primary")}
              >
                {primaryLabel}
              </a>
            ) : (
              <div
                className={`${primaryStaticClassName} pointer-events-none cursor-default focus-visible:ring-0`}
                style={primaryStyle}
              >
                {primaryLabel}
              </div>
            )
          ) : null}

          {profile.secondaries.map((btn) =>
            interactive ? (
              <a
                key={btn.id}
                href={btn.href}
                {...linkExternalProps(btn.href)}
                className={secondaryClassName}
                onClick={() => notifyTrack(`secondary:${btn.id}`)}
              >
                {btn.label}
              </a>
            ) : (
              <div
                key={btn.id}
                className={`${secondaryClassName} pointer-events-none cursor-default`}
              >
                {btn.label}
              </div>
            ),
          )}
        </div>

        <p className="mt-14 text-[10px] font-semibold tracking-wider text-slate-600 uppercase">
          {poweredByLabel}
        </p>
      </div>
    </div>
  );
};

export default LinkInBioPublicView;
